export const FILE_BUCKETS = {
  PUBLIC: 'public',
  PRIVATE: 'private',
  INCOMING: 'incoming',
} as const;

/**
 * The single source of truth for which file types the platform accepts and the
 * maximum upload size. Both the backend allow-list (`FILE_TYPES_DEFINITIONS`,
 * consumed via `@src/constants.js`) and the frontend dropzone (`EXTENSION_TO_MIME`)
 * are derived from the authoritative `FILE_TYPE_REGISTRY` below, so they can never
 * drift apart.
 */
export enum FileType {
  Image = 'IMAGE',
  Video = 'VIDEO',
  Document = 'DOCUMENT',
}

export interface FileDefinition {
  type: FileType;
  extensions: string[];
  contentTypes: string[];
}

interface FileTypeRegistryEntry {
  type: FileType;
  /** lowercase, dot-prefixed extension, e.g. `.mp4` */
  extension: string;
  /** canonical MIME type for the extension */
  contentType: string;
}

/**
 * Authoritative (extension ↔ MIME ↔ category) list. Add new accepted file types
 * here only — the derived structures below pick them up automatically.
 */
export const FILE_TYPE_REGISTRY: FileTypeRegistryEntry[] = [
  // Images
  { type: FileType.Image, extension: '.jpg', contentType: 'image/jpeg' },
  { type: FileType.Image, extension: '.jpeg', contentType: 'image/jpeg' },
  { type: FileType.Image, extension: '.png', contentType: 'image/png' },
  { type: FileType.Image, extension: '.gif', contentType: 'image/gif' },
  { type: FileType.Image, extension: '.webp', contentType: 'image/webp' },
  { type: FileType.Image, extension: '.bmp', contentType: 'image/bmp' },
  { type: FileType.Image, extension: '.tiff', contentType: 'image/tiff' },
  { type: FileType.Image, extension: '.heic', contentType: 'image/heic' },
  // Videos
  { type: FileType.Video, extension: '.mp4', contentType: 'video/mp4' },
  { type: FileType.Video, extension: '.webm', contentType: 'video/webm' },
  { type: FileType.Video, extension: '.mov', contentType: 'video/quicktime' },
  { type: FileType.Video, extension: '.avi', contentType: 'video/x-msvideo' },
  { type: FileType.Video, extension: '.mkv', contentType: 'video/x-matroska' },
  // Documents
  {
    type: FileType.Document,
    extension: '.pdf',
    contentType: 'application/pdf',
  },
  { type: FileType.Document, extension: '.rtf', contentType: 'text/rtf' },
  {
    type: FileType.Document,
    extension: '.doc',
    contentType: 'application/msword',
  },
  {
    type: FileType.Document,
    extension: '.docx',
    contentType:
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  },
  {
    type: FileType.Document,
    extension: '.xls',
    contentType: 'application/vnd.ms-excel',
  },
  {
    type: FileType.Document,
    extension: '.xlsx',
    contentType:
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
  { type: FileType.Document, extension: '.csv', contentType: 'text/csv' },
  {
    type: FileType.Document,
    extension: '.ppt',
    contentType: 'application/vnd.ms-powerpoint',
  },
  {
    type: FileType.Document,
    extension: '.pptx',
    contentType:
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  },
];

/** Derived: extension → canonical MIME type (used by the frontend dropzone). */
export const EXTENSION_TO_MIME: Record<string, string> = Object.fromEntries(
  FILE_TYPE_REGISTRY.map(entry => [entry.extension, entry.contentType]),
);

/**
 * Derived: allow-list grouped by category, preserving registry order with unique
 * extensions/content types (used by the backend upload validation).
 */
export const FILE_TYPES_DEFINITIONS: FileDefinition[] = Object.values(
  FileType,
).map(type => {
  const entries = FILE_TYPE_REGISTRY.filter(entry => entry.type === type);
  return {
    type,
    extensions: [...new Set(entries.map(entry => entry.extension))],
    contentTypes: [...new Set(entries.map(entry => entry.contentType))],
  };
});

/** Extensions accepted for a given file type category. */
export function getExtensionsForFileType(type: FileType): string[] {
  return (
    FILE_TYPES_DEFINITIONS.find(definition => definition.type === type)
      ?.extensions ?? []
  );
}

/** Global maximum upload size in bytes (1 GB). */
export const MAX_UPLOAD_FILE_SIZE_BYTES = 1024 * 1024 * 1024;
